/**
 * The "which side of the marketplace am I using" cookie.
 *
 * Session-scoped (no max-age) — ephemeral UI state that should die when the
 * browser closes. Display-only: route and data access are enforced server-side,
 * so a tampered value buys nothing.
 *
 * Extracted here because two things write it. `useNavContext` derives it from the
 * pathname on every navigation, which covers the ordinary case of a user walking
 * into `/host/...` or `/driver/...`. But a deliberate cross-side jump — the chat
 * context card sending a host to the driver page for their own parking session —
 * has to write it BEFORE navigating, or the destination's server render reads the
 * stale value and paints host chrome for a frame before the client effect
 * corrects it.
 */
export const NAV_CONTEXT_COOKIE = "ezspot_nav_context";

export type NavContext = "host" | "driver";

export function parseNavContext(value: string | undefined): NavContext | null {
  return value === "host" || value === "driver" ? value : null;
}

/**
 * Write the nav context from the browser.
 *
 * Uses the Cookie Store API where available and falls back to `document.cookie`
 * otherwise. The fallback carries `Secure`; the Cookie Store call does not need
 * it because that API refuses to set a cookie on an insecure origin anyway.
 */
export function writeNavContext(context: NavContext): void {
  if (typeof window === "undefined") return;

  if ("cookieStore" in window) {
    void (window.cookieStore as CookieStore).set({
      name: NAV_CONTEXT_COOKIE,
      value: context,
      path: "/",
      sameSite: "lax",
    });
    return;
  }

  // biome-ignore lint/suspicious/noDocumentCookie: intentional fallback for browsers without the Cookie Store API
  document.cookie = `${NAV_CONTEXT_COOKIE}=${context}; path=/; SameSite=Lax; Secure`;
}
